"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isTruncated = isTruncated;
exports.useTooltip = useTooltip;
exports.Tooltip = Tooltip;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const cn_1 = require("../lib/cn");
const overlay_1 = require("../lib/overlay");
/**
 * ⚠️ **True when an element's content is clipped** — the ellipsis case, horizontally, and a
 * `line-clamp` vertically. Measured at the moment of hover, never at rest: a list of hundreds of
 * rows pays nothing until somebody points at one.
 */
function isTruncated(element) {
    return (element.scrollWidth > element.clientWidth + 0.5 ||
        element.scrollHeight > element.clientHeight + 0.5);
}
function hasContent(content) {
    return content !== null && content !== undefined && content !== false && content !== '';
}
/** Whether the browser counts this focus as a keyboard one; an engine without the selector says yes. */
function focusVisible(element) {
    try {
        return element.matches(':focus-visible');
    }
    catch {
        return true;
    }
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE TOOLTIP WITHOUT ITS WRAPPER — WHAT REPLACED EVERY NATIVE `title`. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner's standing rule (20 Oct 2026): **no native tooltips, anywhere, ever.** The
 * browser's own is unstyled, slow, ignores the theme and cannot be dismissed. This hook is how a
 * kit component that used to forward `title` to the DOM — `Button`, `Badge`, the inputs, the table
 * cells — shows the kit's surface instead, on the element itself, with no wrapper to disturb a
 * flex row.
 *
 * ⚠️ **Cheap at rest, on purpose.** One boolean, two refs and an id; the surface, its position
 * tracking and its Escape listener mount only while it is shown. A grid of three hundred cells
 * each carrying one costs three hundred booleans.
 */
function useTooltip(content, options = {}) {
    const { delay = 250, width = 320, side = 'top', when } = options;
    const [shown, setShown] = (0, react_1.useState)(false);
    const timer = (0, react_1.useRef)(null);
    const anchor = (0, react_1.useRef)(null);
    const pressed = (0, react_1.useRef)(false);
    const id = (0, react_1.useId)();
    const has = hasContent(content);
    const show = (element) => {
        if (!has)
            return;
        if (when !== undefined && !when(element))
            return;
        anchor.current = element;
        if (timer.current !== null)
            clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            setShown(true);
        }, delay);
    };
    const hide = () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
        setShown(false);
    };
    /* ⚠️ Clearing on unmount is not tidiness: a row that scrolls out of a virtualised list
     * mid-delay would otherwise call `setShown` on an unmounted component, and in a list of
     * hundreds that is a steady drip of warnings that buries the real one. */
    (0, react_1.useEffect)(() => () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
    }, []);
    const visible = shown && has;
    const triggerProps = (caller) => ({
        onMouseEnter: (event) => {
            caller.onMouseEnter?.(event);
            pressed.current = false;
            show(event.currentTarget);
        },
        onMouseLeave: (event) => {
            caller.onMouseLeave?.(event);
            pressed.current = false;
            hide();
        },
        /* ⚠️ 28 Oct 2026 · only a focus the browser would RING. A dialog opened by a click moves focus
         * to its first control, and the hint flashed over the header the moment it opened (UAT); the
         * same move made from the keyboard is `:focus-visible`, and still shows it. */
        onFocus: (event) => {
            caller.onFocus?.(event);
            if (!pressed.current && focusVisible(event.currentTarget))
                show(event.currentTarget);
        },
        onBlur: (event) => {
            caller.onBlur?.(event);
            hide();
        },
        /* ⚠️ A press dismisses, and the focus that follows it does not bring the hint back: the
         * person has acted, and a hint hanging over the menu they just opened covers its first row. */
        onPointerDown: (event) => {
            caller.onPointerDown?.(event);
            pressed.current = true;
            hide();
        },
        'aria-describedby': [caller['aria-describedby'], visible ? id : undefined].filter(Boolean).join(' ') || undefined,
    });
    return {
        triggerProps,
        bubble: visible ? ((0, jsx_runtime_1.jsx)(TooltipSurface, { id: id, anchor: anchor, width: width, side: side, onDismiss: hide, children: content })) : null,
    };
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Explains an icon-only control or a truncated figure.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A tooltip is not a channel.** `state-rules.md` §10: values are readable *without*
 * hovering. This explains — "weekly capacity: 40:00" beside a figure of 32:00 — it never
 * carries the only copy of something the reader needs.
 *
 * ⚠️ **`focus` as well as `hover`, and Escape dismisses.** Both are WCAG 1.4.13 (Content on
 * Hover or Focus): a tooltip reachable only by pointer is invisible to a keyboard, and one that
 * cannot be dismissed without moving the pointer covers content for a screen-magnifier user.
 *
 * ⚠️ **`aria-describedby`, not `aria-label`.** Describing adds to the control's name; labelling
 * REPLACES it. On a button whose only label is its icon, `aria-label` on a tooltip wrapper would
 * silently override the name the button already has. The surface is in a portal and the
 * association is by id, which is document-wide — so moving it changed nothing here.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ FOUR FAULTS AT ONCE, AND ALL FOUR WERE THIS COMPONENT.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Reported on the money dashboard, where forty of these sit in one screen:
 *
 *  1. **Behind the sidebar and the header.** The surface was `position: absolute` inside the
 *     trigger, so its `z-50` was ranked inside whatever stacking context an ancestor had already
 *     opened — and against the shell's chrome it lost. A z-index only ranks siblings.
 *  2. **Nothing but a sliver of shadow.** The cards clip (`overflow: hidden`), so a surface
 *     hanging above its trigger was cut off at the card's edge; the line people saw was its
 *     bottom shadow escaping.
 *  3. **Purple.** `bg-primary text-primary-foreground` is the design system's tooltip, and it
 *     reads as a badge rather than as an explanation once the content is four paragraphs.
 *  4. **SHOUTING.** The surface was rendered INSIDE the trigger, and the triggers on that screen
 *     are `uppercase tracking-wide` captions — so every hint inherited `text-transform` and came
 *     out in full caps. Nothing in the tooltip asked for that; it was simply downstream.
 *
 * The first two are the same fix — a portal into `document.body`, positioned by `anchorTo`, which
 * already flips and clamps for every menu in the library. The last is why `normal-case` and
 * `tracking-normal` are set explicitly: a portaled surface inherits from `body`, but the classes
 * stay as the record of what went wrong.
 */
function Tooltip({ content, delay = 250, children, className, width = 320, side = 'top', asChild = false, overflowOnly = false, }) {
    const tip = useTooltip(content, overflowOnly ? { delay, width, side, when: isTruncated } : { delay, width, side });
    if (asChild && (0, react_1.isValidElement)(children)) {
        const child = children;
        return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, react_1.cloneElement)(child, tip.triggerProps(child.props)), tip.bubble] }));
    }
    return ((0, jsx_runtime_1.jsxs)("span", { className: (0, cn_1.cn)('relative inline-flex', className), ...tip.triggerProps({}), children: [children, tip.bubble] }));
}
/**
 * The surface itself. Mounted only while shown, so its position tracking (a resize and a
 * capturing scroll listener) and its Escape listener exist for one tooltip at a time.
 */
function TooltipSurface({ id, anchor, width, side, onDismiss, children, }) {
    const { rect } = (0, overlay_1.useAnchorRect)(true, anchor);
    (0, react_1.useEffect)(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                onDismiss();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onDismiss]);
    if (typeof document === 'undefined')
        return null;
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)("span", { id: id, role: "tooltip", 
        /**
         * ⚠️ **`bg-popover`, which is white in light and near-black in dark** — the tokens
         * every other surface that floats over the page already uses, so a hint reads as a
         * sheet of paper rather than as a chip. A border and the pop shadow give it an edge
         * against a card of the same colour underneath.
         */
        className: "pop-in bg-popover text-popover-foreground shadow-pop border-border pointer-events-none block rounded-md border px-2.5 py-1.5 text-[11.5px] leading-snug font-normal tracking-normal whitespace-normal normal-case", style: (0, overlay_1.anchorTo)(rect, { side, align: 'start', width }), children: children }), document.body);
}
//# sourceMappingURL=tooltip.js.map